/**
 * formatApiError(error, fallback)
 *
 * Safely turns an Axios/Fetch error into a readable string suitable for
 * toast.error(). Handles three shapes of `error.response.data.detail`:
 *   - string  -> returned as-is
 *   - array   -> Pydantic 422 validation list; flattened to "field: msg"
 *   - object  -> stringified via msg/message/JSON
 * Falls back to `error.message` and finally to the supplied fallback.
 *
 * Always returns a string, so it is safe to pass directly to React children
 * and to sonner's toast.error().
 */
export const formatApiError = (error, fallback = 'Something went wrong') => {
    const detail = error?.response?.data?.detail;

    if (typeof detail === 'string' && detail.trim()) {
        return detail;
    }

    if (Array.isArray(detail)) {
        const parts = detail
            .map((e) => {
                if (!e) return null;
                if (typeof e === 'string') return e;
                const loc = Array.isArray(e.loc) ? e.loc.filter((p) => p !== 'body') : [];
                const field = loc.join('.');
                const msg = e.msg || e.message || 'Invalid value';
                return field ? `${field}: ${msg}` : msg;
            })
            .filter(Boolean);
        if (parts.length) return parts.join('; ');
    }

    if (detail && typeof detail === 'object') {
        if (typeof detail.msg === 'string') return detail.msg;
        if (typeof detail.message === 'string') return detail.message;
        try {
            return JSON.stringify(detail);
        } catch {
            // fall through
        }
    }

    // Handle slowapi rate-limit responses which use { "error": "..." } not { "detail": "..." }
    const errorField = error?.response?.data?.error;
    if (typeof errorField === 'string' && errorField.trim()) {
        return errorField;
    }

    if (typeof error?.message === 'string' && error.message.trim()) {
        return error.message;
    }

    return fallback;
};

export default formatApiError;
